/**
 * What the app does with a parsed deep link (#1599 Story 3.3).
 *
 * Pure, so the decision is testable outside App.tsx, which applies it:
 *
 * - `'defer'`: nobody is signed in. The route is saved as the return URL and
 *   the post-sign-in redirect lands on it, as before.
 * - `'navigate'`: the ordinary path, `navigate(route)`.
 * - `'reopen'`: the link names the request ALREADY on screen. `navigate` would
 *   change the route string and nothing else, because `RequestDetail` reads
 *   `discussion` and `thread` once, on mount (Story 2.3: "read once, on mount,
 *   so a later close is not undone"), and the correspondence session seeds the
 *   arrival only when it mints a store. So App.tsx re-mounts the screen, after
 *   the request leave guard agrees, and the existing reader runs as a fresh
 *   arrival. There is deliberately no second, imperative "arrive now" reader.
 */

export type DeepLinkAction = 'defer' | 'navigate' | 'reopen';

const REQUEST_DETAIL_PATH = '/request-detail';

const requestIdOf = (route: string): string | null => {
  const [path, query] = route.split('?');
  if (path !== REQUEST_DETAIL_PATH) {
    return null;
  }
  const id = new URLSearchParams(query ?? '').get('id');
  return id === null || id === '' ? null : id;
};

export const deepLinkAction = ({
  route,
  isAuthenticated,
  currentRoute,
}: {
  /** The parsed, validated internal route (`parseDeepLink(...).route`). */
  route: string;
  isAuthenticated: boolean;
  /** The route on screen now, query included (native `__currentRoute`). */
  currentRoute: string;
}): DeepLinkAction => {
  if (!isAuthenticated) {
    return 'defer';
  }
  const linkedId = requestIdOf(route);
  if (linkedId !== null && linkedId === requestIdOf(currentRoute)) {
    return 'reopen';
  }
  return 'navigate';
};
